import React, { FC, useCallback, useState } from 'react';

import { Empty, KVRows, StatusChip } from '../bits';
import { ago, feeState, hms, isoStamp, money, until } from '../data/format';
import { IAlert, THRESHOLDS } from '../data/types';
import {
	RECORD_FORMATS,
	useTmActions,
	useTransaction,
	useTransactionReport,
	useSarDraft,
	useAssignees,
	useAssignCase,
} from '../data/use-tm';
import { SarDraftSection } from './sar-draft';
import { SarModal } from './sar-modal';

const ALERT_STATES = [
	'OPEN',
	'INVESTIGATING',
	'AWAITING_USER',
	'PENDING_SAR',
	'SAR_FILED',
	'RESOLVED',
	'DISMISSED',
];

/**
 * The alert drawer: why the transaction was flagged, what it is, who it is, and
 * every decision anyone has made on it.
 *
 * "Why" comes FIRST and is the only section that cannot be empty. It is the
 * question an analyst opened the drawer to answer, and the matched rule runs are
 * the audit trail behind the score — the arithmetic is printed out
 * (`25 + 35 = 60`) rather than only stated, so a disputed decision can be
 * checked against the catalogue without a database.
 */
export const TxnDrawer: FC<{ id: string | null; onClose: () => void; onChanged: () => void }> = ({
	id,
	onClose,
	onChanged,
}) => {
	const { txn, reload } = useTransaction(id);
	const sar = useSarDraft(id);
	const assignees = useAssignees();
	const assignCase = useAssignCase();
	const [sarOpen, setSarOpen] = useState(false);
	const [opening, setOpening] = useState(false);
	const { decide, updateAlert } = useTmActions();
	const { download, busy: downloading } = useTransactionReport();
	const [note, setNote] = useState('');
	const [dlOpen, setDlOpen] = useState(false);

	const run = useCallback(
		async (fn: Promise<unknown>) => {
			await fn;
			/* The SAR draft reloads with the transaction: an escalation widens it
			   to the case, and a decision changes what it says. */
			await Promise.all([reload(), sar.reload()]);
			onChanged();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[reload, onChanged, sar.reload]
	);

	if (!id) return null;
	if (!txn) {
		return (
			<aside className="drawer open" aria-label="Alert details">
				<div className="dhead">
					<div className="dt">
						<h2>Loading…</h2>
						<button className="dx" onClick={onClose} aria-label="Close details">
							✕
						</button>
					</div>
				</div>
			</aside>
		);
	}

	const alert: IAlert | undefined = txn.alerts?.[0];
	const matched = (txn.ruleRuns ?? []).filter(r => r.matched);
	const cleanCount = (txn.ruleRuns ?? []).length - matched.length;
	const scoring = matched.filter(r => r.mode === 'ACTIVE');
	const p: any = txn.applicant ?? {};
	/* EVERY provider result, not just the first. This read `providerResults[0]`,
	   which is correct for the single-provider case and silently hides the rest —
	   and a transaction screened by two providers that DISAGREE is precisely the
	   one an analyst opened this drawer to look at. */
	const providers: any[] = txn.providerResults ?? [];

	return (
		<aside className="drawer open" aria-label="Alert details">
			{/* THE VERY TOP: who owns this, and the SAR. The two things an analyst
			    reaches for first on a flagged transaction, so they sit above the
			    title rather than among the decisions further down. "Open SAR"
			    escalates first when there is no case yet — the SAR is written
			    against a case, and the pop-out needs one to save to. */}
			<div className="dtop" data-qa="drawer-top-actions">
				<label className="dtop-assign">
					<span>Assigned to</span>
					<select
						value={alert?.assignedTo ?? ''}
						disabled={!alert}
						onChange={e => {
							const name = e.target.value;
							if (alert && name) run(assignCase(alert._id, sar.draft?.caseId ?? null, name));
						}}
						data-qa="drawer-assign"
					>
						<option value="">{alert ? 'Unassigned' : 'No alert to assign'}</option>
						{assignees.map(u => {
							const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u._id;
							return (
								<option key={u._id} value={name}>
									{name}
								</option>
							);
						})}
						{alert?.assignedTo && !assignees.some(u => [u.firstName, u.lastName].filter(Boolean).join(' ') === alert.assignedTo) && (
							<option value={alert.assignedTo}>{alert.assignedTo}</option>
						)}
					</select>
				</label>
				<button
					className="dbtn mag"
					type="button"
					disabled={!alert || opening}
					title={alert ? '' : 'Only a transaction with an alert can be escalated to a SAR'}
					data-qa="drawer-open-sar"
					onClick={async () => {
						if (!alert) return;
						setOpening(true);
						if (!sar.draft?.caseId) await run(updateAlert(alert._id, { escalate: true }));
						setOpening(false);
						setSarOpen(true);
					}}
				>
					{opening ? 'Opening…' : sar.draft?.caseId ? 'Open SAR' : 'Escalate & open SAR'}
				</button>
			</div>
			{/* Splits the owner-and-SAR bar from the transaction below it. */}
			<div className="dtop-rule" aria-hidden="true">
				~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
			</div>
			{sarOpen && sar.draft && (
				<SarModal
					draft={sar.draft}
					onClose={() => setSarOpen(false)}
					onSave={sar.save}
					onGenerateXml={sar.generateXml}
				/>
			)}

			<div className="dhead">
				<div className="dt">
					<h2>{txn.subjectName}</h2>
					<StatusChip status={txn.status} />
					<span className={`sev ${txn.severity.toLowerCase()}`}>
						{txn.severity} {txn.score}
					</span>
					<button className="dx" onClick={onClose} aria-label="Close details">
						✕
					</button>
				</div>

				{/* The alert's own state, and the only place it can be set directly.
				    This chain was drawn as a read-only progress indicator while
				    `PATCH /alerts/:id` accepted `status` all along — so INVESTIGATING,
				    RESOLVED and DISMISSED were reachable only as side effects of other
				    buttons, and SAR_FILED only by filing a SAR from the other deck.
				    Each state is now the control that sets it.

				    PENDING_SAR is deliberately NOT settable here: escalating is what
				    opens or joins the case the SAR is written against, and stamping the
				    status by hand would leave an alert pending a SAR with no case
				    behind it. "Escalate to case" above is that transition. */}
				{alert && (
					<div className="dsteps">
						{ALERT_STATES.map(s => {
							const now = s === alert.status;
							const settable = !now && s !== 'PENDING_SAR' && s !== 'SAR_FILED';
							return (
								<button
									key={s}
									type="button"
									className={`dstep${now ? ' on' : ''}`}
									disabled={!settable}
									title={
										now
											? 'Current state'
											: settable
												? `Move this alert to ${s.replace('_', ' ').toLowerCase()}`
												: s === 'PENDING_SAR'
													? 'Set by escalating to a case'
													: 'Set by filing the SAR on the case'
									}
									onClick={() => settable && run(updateAlert(alert._id, { status: s }))}
								>
									<i className={now ? 'dot-rev' : 'dot-pend'} />
									{s.replace('_', ' ').toLowerCase()}
									{now ? ' (now)' : ''}
								</button>
							);
						})}
					</div>
				)}

				<div className="dbtns">
					<button className="dbtn ok" type="button" onClick={() => run(decide(txn._id, 'APPROVED', 'analyst_approved'))}>
						Approve
					</button>
					<button className="dbtn danger" type="button" onClick={() => run(decide(txn._id, 'DECLINED', 'analyst_declined'))}>
						Decline
					</button>
					<button className="dbtn pri" type="button" onClick={() => run(decide(txn._id, 'AWAITING_USER', 'rule_change_status'))}>
						Request user info
					</button>
					<button className="dbtn" type="button" onClick={() => run(decide(txn._id, 'APPROVED', 'analyst_cleared'))}>
						Dismiss (false positive)
					</button>
					{/* "Escalate to case" moved to the very top of the drawer, as
					    "Escalate & open SAR" — see the bar above the title. */}

					{/* THE TRANSACTION RECORD — this transaction as a document a
					    compliance function can rely on, in the same three formats a
					    session dossier is issued in.

					    It sits with the DECISIONS rather than down beside the
					    evidence because that is when it is wanted: an analyst
					    approving, declining or escalating is the person who needs the
					    record of what they just decided, and making them scroll for it
					    is how a record gets exported later from memory instead. */}
					<div className="dlmenu">
						<button
							className="dbtn"
							type="button"
							disabled={downloading}
							aria-expanded={dlOpen}
							title="Download this transaction as a compliance record"
							onClick={() => setDlOpen(o => !o)}
						>
							{downloading ? 'Building…' : 'Record ▾'}
						</button>
						{dlOpen && !downloading && (
							<div className="dlmenu__list">
								{RECORD_FORMATS.map(f => (
									<button
										key={f.value}
										type="button"
										className="dlmenu__item"
										onClick={() => {
											setDlOpen(false);
											download([txn._id], f.value);
										}}
									>
										{f.label}
									</button>
								))}
							</div>
						)}
					</div>
				</div>

				{alert && (
					<div className="dleg">
						Alert · <span className="mono">{alert._id}</span> · source {alert.source} ·{' '}
						{/* A historical finding carries no SLA — see IAlert.dueAt. The drawer
						    is reachable for one via the import page, so this says so rather
						    than formatting a null into the epoch. */}
						{alert.dueAt ? (
							<>
								due <b className="mono">{until(new Date(alert.dueAt).getTime())}</b>
							</>
						) : (
							<b className="mono">no SLA · historical</b>
						)}
						{alert.linkedCaseId && (
							<>
								{' · case '}
								<b className="mono">{alert.linkedCaseId}</b>
							</>
						)}
						{alert.assignedTo ? ` · ${alert.assignedTo}` : ' · '}
						{!alert.assignedTo && <span className="rd">unassigned</span>}
					</div>
				)}
			</div>

			<div className="dsec">
				<div className="sh">
					Why it was flagged{' '}
					<span className="r2">
						{matched.length} matched · {cleanCount} evaluated clean
					</span>
				</div>
				{matched.length ? (
					matched.map(r => (
						<div className={`rr${r.mode === 'TEST' ? ' test' : ''}`} key={r.ruleUuid}>
							<div>
								<div className="rn">
									{r.ruleName}
									{r.actionSummary.includes('change_status') && <span className="nd fail">sets status</span>}
									{r.mode === 'TEST' && <span className="nd test">test</span>}
								</div>
								<span className="rb">
									{r.bundle} · {r.actionSummary.join(', ')}
									{r.matchedPayload && (
										<>
											{' · '}
											{Object.entries(r.matchedPayload)
												.map(
													([k, v]) =>
														`${k}=${
															typeof v === 'number'
																? Math.round(v).toLocaleString()
																: Array.isArray(v)
																	? v.join('|')
																	: String(v)
														}`
												)
												.join(' ')}
										</>
									)}
								</span>
							</div>
							<div className="rbar">
								<i style={{ width: `${Math.min(100, (r.scoreDelta / 60) * 100)}%` }} />
							</div>
							<div className="rd2">{r.mode === 'TEST' ? '±0' : `+${r.scoreDelta}`}</div>
						</div>
					))
				) : (
					<Empty>No rule matched — this alert was opened manually.</Empty>
				)}
				<div className="rrsum">
					score <b>{txn.score}</b> = {scoring.map(r => r.scoreDelta).join(' + ') || '0'}
					<span className="thr">
						review ≥ {THRESHOLDS.review} · decline ≥ {THRESHOLDS.decline}
					</span>
				</div>
			</div>

			{/* The pre-filled SAR sits directly under the evidence it is built
			    from, so an analyst reads "why it was flagged" and then "what that
			    means for a filing" without scrolling. */}
			<SarDraftSection draft={sar.draft} loading={sar.loading} />

			<div className="dsec">
				<div className="sh">
					Transaction <span className="r2">{txn.txnId}</span>
				</div>
				<table className="kv wide">
					<tbody>
						<KVRows
							rows={[
								['id', <span key="id" className="mono">{txn._id}</span>],
								[
									'kind',
									txn.transactionKind === 'trade' ? (
										<span key="k" className="nd review">trade</span>
									) : (
										'payment'
									),
								],
								/* JOINED ONLY WHERE BOTH EXIST. A template literal prints the word
								   "undefined" for a missing half, and this row showed
								   "finance · undefined" on every transaction ingested without an
								   actionType — which reads as a stored value rather than an absent
								   one. KVRows already drops a null row, so the pair disappears
								   instead of being asserted. */
								[
									'type',
									[txn.transactionType, txn.actionType].filter(Boolean).join(' · ') ||
										null,
								],
								[
									/* A TRADE HAS NO SINGLE DIRECTION — the subject sends one
									   leg and receives the other at the same moment. Showing
									   the stored `direction` for one would describe the trade
									   as if it were a payment. */
									'direction',
									txn.transactionKind === 'trade' ? 'both — see sent / received' : txn.direction,
								],
								...(txn.transactionKind === 'trade' && txn.legs?.sent
									? ([
											[
												'sent',
												<>
													{money(
														txn.legs.sent.amount,
														txn.legs.sent.currency,
														txn.legs.sent.currencyKind
													)}
													{txn.legs.sent.preferredCurrencyAmount !== undefined && (
														<span className="gy">
															{' '}
															(≈ ${txn.legs.sent.preferredCurrencyAmount.toLocaleString()})
														</span>
													)}
													{/* THIS LEG'S OWN FEE. A venue that takes its cut on only
													    one side is common, and a single combined number gives
													    the reader no way to tell which side it came from. */}
													{txn.legs.sent?.feeAmount ? (
														<span className="gy">
															{' '}
															· fee{' '}
															{money(
																txn.legs.sent.feeAmount,
																txn.legs.sent.feeCurrency ?? txn.legs.sent.currency
															)}
														</span>
													) : null}
												</>,
											],
											[
												'received',
												<>
													{money(
														txn.legs.received?.amount ?? 0,
														txn.legs.received?.currency ?? '',
														txn.legs.received?.currencyKind
													)}
													{txn.legs.received?.preferredCurrencyAmount !== undefined && (
														<span className="gy">
															{' '}
															(≈ ${txn.legs.received.preferredCurrencyAmount.toLocaleString()})
														</span>
													)}
													{/* THIS LEG'S OWN FEE — see the sent leg above. */}
													{txn.legs.received?.feeAmount ? (
														<span className="gy">
															{' '}
															· fee{' '}
															{money(
																txn.legs.received.feeAmount,
																txn.legs.received.feeCurrency ?? txn.legs.received.currency
															)}
														</span>
													) : null}
												</>,
											],
											[
												'scored against',
												<>
													${txn.preferredCurrencyAmount.toLocaleString()}
													<span className="gy"> — the greater of the two legs</span>
												</>,
											],
										] as [string, React.ReactNode][])
									: ([
											[
												'amount',
												<>
													{money(txn.amount, txn.currency, txn.currencyKind)}
													{txn.currencyKind === 'crypto' && (
														<span className="gy">
															{' '}
															(≈ ${txn.preferredCurrencyAmount.toLocaleString()})
														</span>
													)}
												</>,
											],
										] as [string, React.ReactNode][])),
								/* THE THREE FIGURES, always in this order: what moved, what
								   it cost, and what the customer paid in total. The fee row is
								   dropped when there is none — KVRows omits an empty value —
								   rather than printing "$0.00", which reads as a fee that was
								   charged and happened to be zero. */
								/* AN EXPLICIT ZERO IS NOT MISSING DATA. This gate used to be a
								   truthiness test on the summed `feePreferredCurrencyAmount`,
								   which is 0 both when nobody supplied a fee and when a client
								   reported `feeAmount: 0` — "we checked, nothing was charged".
								   Both vanished, so an honestly reported zero was indistinguishable
								   from silence. `feeState` reads the RAW fields, where `null` and
								   `"0"` are already different, and says which of the three cases
								   this is.
								
								   The row is named 'total fee' whenever a per-leg breakdown appears
								   on the sent/received rows above, so its own label says it is the
								   sum of what was just itemized rather than a fourth figure. */
								[
									txn.legs?.sent?.feeAmount || txn.legs?.received?.feeAmount
										? 'total fee'
										: 'fee',
									feeState(txn) === 'unsupplied' ? null : feeState(txn) === 'zero' ? (
										<>
											$0.00<span className="gy"> — no fee charged</span>
										</>
									) : (
										<>
											{txn.feeAmount && txn.feeCurrency
												? money(txn.feeAmount, txn.feeCurrency)
												: `$${(txn.feePreferredCurrencyAmount ?? 0).toLocaleString()}`}
											{txn.feeAmount && txn.feeCurrency && (
												<span className="gy">
													{' '}
													(≈ ${(txn.feePreferredCurrencyAmount ?? 0).toLocaleString()})
												</span>
											)}
										</>
									),
								],
								[
									'total with fee',
									txn.preferredCurrencyTotalAmount &&
									txn.preferredCurrencyTotalAmount !== txn.preferredCurrencyAmount ? (
										<>
											${txn.preferredCurrencyTotalAmount.toLocaleString()}
											<span className="gy"> — reported, not scored</span>
										</>
									) : null,
								],
								['payment details', txn.paymentDetails],
								['txn date', isoStamp(txn.txnDate)],
								[
									'decision reason',
									txn.decisionReasonCode ? (
											<span key="reason" className="mono">
												{txn.decisionReasonCode}
											</span>
										) : null,
								],
							]}
						/>
					</tbody>
				</table>
			</div>

			<div className="dsec">
				<div className="sh">
					Subject <span className="r2">{txn.subjectVendorData}</span>
				</div>
				<table className="kv wide">
					<tbody>
						<KVRows
							rows={[
								['full name', txn.subjectName],
								['entity type', txn.subjectEntityType],
								['country', txn.subjectCountry],
								/* Same trap: no counterparty name printed "undefined · unhosted". */
								[
									'counterparty',
									txn.counterpartyName
										? `${txn.counterpartyName} · ${txn.counterpartyCountry ?? 'unhosted'}`
										: null,
								],
								/* SENDER AND RECIPIENT are DERIVED, never stored — they are
								   the subject/counterparty pair read through the direction.
								   Storing them would be a third vocabulary alongside
								   subject/counterparty and the travel rule's own
								   originator/beneficiary, and the three would drift.
								
								   A TRADE GETS FOUR ROWS, ONE PAIR PER LEG, because on a trade
								   the subject is simultaneously the sender of one leg and the
								   recipient of the other — a single pair can only say that by
								   naming the subject on both sides, which is what this did
								   before and which hid the counterparty entirely. For Austin's
								   own example the four rows read: sender (BTC leg) = the
								   customer, recipient (BTC leg) = liquidity.io, sender (USD
								   leg) = liquidity.io, recipient (USD leg) = the customer —
								   legible without the reader mentally inverting `direction`.
								
								   Each leg may name its OWN counterparty (`legs.*.counterpartyName`);
								   the common trading-desk case leaves that unset and inherits
								   the transaction's, which is why it falls back rather than
								   printing blank. */
								...(txn.transactionKind === 'trade' && txn.legs?.sent && txn.legs?.received
									? ([
											[
												`sender (${txn.legs.sent.currency} leg)`,
												`${txn.subjectName} · ${txn.subjectCountry ?? '—'}`,
											],
											[
												`recipient (${txn.legs.sent.currency} leg)`,
												`${txn.legs.sent.counterpartyName ?? txn.counterpartyName} · ${
													txn.legs.sent.counterpartyCountry ?? txn.counterpartyCountry ?? 'unhosted'
												}`,
											],
											[
												`sender (${txn.legs.received.currency} leg)`,
												`${txn.legs.received.counterpartyName ?? txn.counterpartyName} · ${
													txn.legs.received.counterpartyCountry ??
													txn.counterpartyCountry ??
													'unhosted'
												}`,
											],
											[
												`recipient (${txn.legs.received.currency} leg)`,
												`${txn.subjectName} · ${txn.subjectCountry ?? '—'}`,
											],
										] as [string, React.ReactNode][])
									: ([
											[
												'sender',
												txn.direction === 'OUTBOUND' ? txn.subjectName : txn.counterpartyName,
											],
											[
												'recipient',
												txn.direction === 'OUTBOUND' ? txn.counterpartyName : txn.subjectName,
											],
										] as [string, React.ReactNode][])),
							]}
						/>
					</tbody>
				</table>
				{/**
				  * TRAVEL RULE (FATF R.16).
				  *
				  * The drawer never showed these at all — the only place the field
				  * names appeared was the historical page's CSV documentation — so an
				  * analyst looking at a transfer held in AWAITING_USER by a
				  * travel-rule predicate could not see WHICH fact was missing without
				  * reading the rule runs.
				  *
				  * NOT ASSESSED IS NOT NON-COMPLIANT. The predicates fire only on an
				  * explicit `false`, so the three states are rendered as three
				  * different things: `yes`, `no`, and an absent row. A flag nobody has
				  * assessed is omitted rather than drawn as a failure, and if NOTHING
				  * was assessed the whole section disappears instead of showing four
				  * empty rows that read like four failures.
				  *
				  * A leg's own facts win over the transaction's, matching the
				  * predicates: on a trade the compliance-relevant side is a specific
				  * leg, not the transaction as a whole.
				  */}
				{(() => {
					/* `engineInput.travelRule` is the authoritative copy — see the
					   type. The top-level field is checked first only so a future
					   mirror is picked up without another change here; today it is
					   always absent and the engineInput copy is what renders. */
					const tr = {
						...(txn.engineInput?.travelRule ?? {}),
						...(txn.travelRule ?? {}),
						...(txn.legs?.sent?.travelRule ?? {}),
						...(txn.legs?.received?.travelRule ?? {}),
					};
					const rows = (
						[
							['counterparty VASP identified', tr.counterpartyVaspIdentified],
							['originator info complete', tr.originatorInfoComplete],
							['beneficiary info complete', tr.beneficiaryInfoComplete],
							['originator name matches KYC', tr.originatorNameMatchesKyc],
						] as [string, boolean | undefined][]
					).filter(([, v]) => v !== undefined);
					if (!rows.length) return null;
					return (
						<>
							<div className="grp">Travel rule</div>
							<table className="kv wide">
								<tbody>
									<KVRows
										rows={rows.map(([label, v]) => [
											label,
											<span key={label} className={v ? 'g' : 'r'}>
												{v ? 'yes' : 'no'}
											</span>,
										])}
									/>
								</tbody>
							</table>
						</>
					);
				})()}
				{(p.fingerprint || p.ipAddress || p.ip_address) && (
					<>
						<div className="grp">Device &amp; network</div>
						<table className="kv wide">
							<tbody>
								<KVRows
									rows={[
										[
										'fingerprint',
										p.fingerprint ? <span key="fp" className="mono">{p.fingerprint}</span> : null,
									],
										[
											'ip',
											p.ipAddress || p.ip_address ? (
												<span key="ip" className="mono">{p.ipAddress || p.ip_address}</span>
											) : null,
										],
										[
											'vpn / tor',
											p.isVpnOrTor === undefined ? null : p.isVpnOrTor ? (
												<span key="vpn" className="rd">true</span>
											) : (
												<span key="vpn" className="g">false</span>
											),
										],
										['asn', p.asnOrganization],
									]}
								/>
							</tbody>
						</table>
					</>
				)}
			</div>

			{/* SCREENING, COUNTRY AND WALLET — the evidence behind a decline.
			    Every one of these was STORED and rendered NOWHERE. An analyst
			    looking at a DECLINED transaction could read the rule name that
			    fired ("Sanctioned wallet exposure") and could not see the
			    address, the list, the entity or the sanctions programme behind
			    it — the facts the decision actually rests on, and the ones a
			    filing has to name. The rule name is the conclusion; this is the
			    evidence.
			
			    Each block renders only when its field is present. Absent means
			    the control did not run — which is a different statement from
			    'it ran and found nothing', and an empty table here would make
			    the second claim on the first one's behalf. */}
			{(txn.engineInput?.screening?.sanctionsMatch ||
				txn.engineInput?.screening?.pepMatch ||
				txn.ofacCryptoHit ||
				txn.countryCoverage ||
				txn.countryRisk ||
				txn.subjectWalletAddress ||
				txn.counterpartyWalletAddress) && (
				<div className="dsec">
					<div className="sh">Screening evidence</div>
					{txn.engineInput?.screening?.sanctionsMatch && (
						<>
							<div className="grp">Sanctions match</div>
							<table className="kv wide">
								<tbody>
									<KVRows
										rows={[
											['list', txn.engineInput.screening.sanctionsMatch.listName],
											['matched name', txn.engineInput.screening.sanctionsMatch.matchedName],
											[
												'match score',
												txn.engineInput.screening.sanctionsMatch.score == null ? null : (
													<span key="ms" className="rd">
														{txn.engineInput.screening.sanctionsMatch.score}
													</span>
												),
											],
										]}
									/>
								</tbody>
							</table>
						</>
					)}
					{txn.engineInput?.screening?.pepMatch && (
						<>
							<div className="grp">PEP match</div>
							<table className="kv wide">
								<tbody>
									<KVRows
										rows={Object.entries(
											txn.engineInput.screening.pepMatch as Record<string, unknown>
										).map(([k, v]) => [
											k,
											typeof v === 'object' ? JSON.stringify(v) : String(v),
										])}
									/>
								</tbody>
							</table>
						</>
					)}
					{/* THE OFAC HIT, named in full. `programs` is the part that decides
					    what a report has to say, so it is not summarised away. */}
					{txn.ofacCryptoHit && (
						<>
							<div className="grp">OFAC wallet screening</div>
							<table className="kv wide">
								<tbody>
									<KVRows
										rows={[
											[
												'entity',
												<span key="e" className="rd">{txn.ofacCryptoHit.entityName}</span>,
											],
											['OFAC uid', txn.ofacCryptoHit.ofacUid],
											['programs', (txn.ofacCryptoHit.programs ?? []).join(', ') || null],
											[
												'matched address',
												<span key="a" className="mono">{txn.ofacCryptoHit.matchedAddress}</span>,
											],
											[
												'chain',
												txn.ofacCryptoHit.normalizedChainCode ?? txn.ofacCryptoHit.sourceChainCode,
											],
											['matched on', txn.ofacCryptoHit.field],
										]}
									/>
								</tbody>
							</table>
						</>
					)}
					{(txn.subjectWalletAddress || txn.counterpartyWalletAddress) && (
						<>
							<div className="grp">Wallet addresses screened</div>
							<table className="kv wide">
								<tbody>
									<KVRows
										rows={[
											[
												'subject wallet',
												txn.subjectWalletAddress ? (
													<span key="sw" className="mono">{txn.subjectWalletAddress}</span>
												) : null,
											],
											[
												'counterparty wallet',
												txn.counterpartyWalletAddress ? (
													<span key="cw" className="mono">{txn.counterpartyWalletAddress}</span>
												) : null,
											],
										]}
									/>
								</tbody>
							</table>
						</>
					)}
					{/* COUNTRY, AND WHY. `reason: 'locked'` is an embargo the business
					    cannot switch off, which is a different fact from a country it
					    has simply not enabled — so the reason is shown, not just the
					    verdict. */}
					{(txn.countryCoverage || txn.countryRisk) && (
						<>
							<div className="grp">Country</div>
							<table className="kv wide">
								<tbody>
									<KVRows
										rows={[
											[
												'country',
												txn.countryCoverage?.countryCode ?? txn.countryRisk?.countryCode,
											],
											[
												'coverage',
												!txn.countryCoverage ? null : txn.countryCoverage.blocked ? (
													<span key="cc" className="rd">
														blocked · {txn.countryCoverage.reason}
													</span>
												) : (
													<span key="cc" className="g">covered</span>
												),
											],
											[
												'risk tier',
												!txn.countryRisk ? null : (
													<span
														key="cr"
														className={
															txn.countryRisk.tier === 'high'
																? 'rd'
																: txn.countryRisk.tier === 'medium'
																	? 'a'
																	: 'g'
														}
													>
														{txn.countryRisk.tier} · {txn.countryRisk.score}
													</span>
												),
											],
										]}
									/>
								</tbody>
							</table>
						</>
					)}
				</div>
			)}

			{/* WHAT MONITORING THIS COST. Stored on every transaction and shown
			    on none of them. `metered: false` with a reason is kept as
			    carefully as a charge: an unbilled transaction is the outcome for
			    an import, for a business with no subscription and for a billing
			    outage, and a month later those three have to be tellable apart
			    when the revenue does not add up. */}
			{txn.billing && (
				<div className="dsec">
					<div className="sh">Billing</div>
					<table className="kv wide">
						<tbody>
							<KVRows
								rows={[
									[
										'metered',
										txn.billing.metered ? (
											<span key="bm" className="g">yes</span>
										) : (
											<span key="bm" className="gy">no</span>
										),
									],
									['price', txn.billing.metered ? `$${txn.billing.price}` : null],
									[
										'not metered because',
										txn.billing.metered ? null : txn.billing.reason,
									],
									['usage record', txn.billing.usageRecordId],
									['at', txn.billing.at ? isoStamp(txn.billing.at) : null],
								]}
							/>
						</tbody>
					</table>
				</div>
			)}

			{providers.map((pr, i) => (
				<div className="dsec" key={pr.provider ?? i}>
					<div className="sh">
						Provider results <span className="r2">{pr.provider}</span>
					</div>
					<table className="kv wide">
						<tbody>
							<KVRows
								rows={[
									['status', pr.status],
									[
										'risk score',
										pr.riskScore === undefined || pr.riskScore === null ? null : (
											<span
												key="risk"
												className={pr.riskScore >= 70 ? 'rd' : pr.riskScore >= 40 ? 'a' : 'g'}
											>
												{pr.riskScore} · {pr.severity}
											</span>
										),
									],
									[
										'sanctions hit',
										/* Rendered only when the provider actually answered. `undefined`
										   here means "not screened for sanctions", and printing that as
										   a green `false` is a clean result the provider never gave. */
										pr.sanctionsHit === undefined || pr.sanctionsHit === null ? null : pr
												.sanctionsHit ? (
											<span key="sanc" className="rd">true</span>
										) : (
											<span key="sanc" className="g">false</span>
										),
									],
								]}
							/>
						</tbody>
					</table>
				</div>
			))}

			<div className="dsec">
				<div className="sh">
					All activity <span className="r2">{txn.activity?.length ?? 0} events</span>
				</div>
				{(txn.activity ?? [])
					.slice()
					.reverse()
					.map(x => (
						<div className="act" key={`${x.at}-${x.who}-${x.text}`}>
							{x.comment ? <span className="act__cmt">{x.text}</span> : x.text} — <b>{x.who}</b>
							<span className="at">
								{hms(new Date(x.at).getTime())} · {ago(new Date(x.at).getTime())} ago
							</span>
						</div>
					))}
				<div className="cmt">
					<textarea
						value={note}
						onChange={e => setNote(e.target.value)}
						placeholder={
							alert
								? "Add a note — recorded on the transaction's activity"
								: 'Notes are recorded against the alert; this transaction has none'
						}
						disabled={!alert}
					/>
					<div className="opts">
						{/* The button was disabled with no alert and the textarea was not, so a
						    note could be typed in full against a transaction that has no alert
						    and then simply refuse to send, with nothing saying why. The note
						    route is `PATCH /alerts/:id` — it needs an alert — so the field says
						    so up front instead. */}
						{!alert && (
							<span className="gy">No alert on this transaction — nothing to note against.</span>
						)}
						<button
							className="dbtn pri"
							type="button"
							style={{ marginLeft: 'auto' }}
							disabled={!note.trim() || !alert}
							onClick={() => {
								if (!alert) return;
								run(updateAlert(alert._id, { note: note.trim() }));
								setNote('');
							}}
						>
							Add note
						</button>
					</div>
				</div>
			</div>
		</aside>
	);
};
